import axios from 'axios';

import config from 'shared/assets/config';
import { getSessionId } from './useSessionApi';

interface BrowserErrorPayload {
  Message: string;
  Detail: string;
  Url: string;
}

interface ReportBrowserErrorOptions {
  applicationId?: string;
  componentStack?: string;
  additionalDetail?: string;
  fallbackMessage?: string;
  url?: string;
}

const applicationIdPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

const recentlyReportedErrors = new Map<string, number>();
const duplicateWindowMs = 5000;

let removeGlobalBrowserErrorHandlers: (() => void) | null = null;

const serializeUnknownError = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (error === null) return 'null';
  if (error === undefined) return '';

  try {
    const serialized = JSON.stringify(error);
    if (serialized && serialized !== '{}') return serialized;
  } catch {
    // Fall back to String for values that cannot be JSON serialized.
  }

  try {
    return String(error);
  } catch {
    return '';
  }
};

const getApplicationIdFromUrl = (url: string): string | null => {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split('/').filter(Boolean);

    if (segments.length === 0) return null;

    const applicationId = segments[segments.length - 1];
    return applicationIdPattern.test(applicationId) ? applicationId : null;
  } catch {
    return null;
  }
};

const createPayload = (
  error: unknown,
  url: string,
  options: ReportBrowserErrorOptions
): BrowserErrorPayload => {
  const serializedError = serializeUnknownError(error);
  const message =
    error instanceof Error
      ? error.toString()
      : serializedError || options.fallbackMessage || 'Unknown browser error';

  const details: string[] = [];
  if (error instanceof Error && error.stack) {
    details.push(error.stack);
  } else if (serializedError) {
    details.push(serializedError);
  }

  if (options.componentStack?.trim()) {
    details.push(`React component stack:${options.componentStack}`);
  }

  if (options.additionalDetail?.trim()) {
    details.push(options.additionalDetail);
  }

  return {
    Message: message,
    Detail: details.join('\n\n') || message,
    Url: url,
  };
};

const isDuplicateReport = (
  applicationId: string,
  error: unknown,
  payload: BrowserErrorPayload
): boolean => {
  const now = Date.now();

  recentlyReportedErrors.forEach((reportedAt, fingerprint) => {
    if (now - reportedAt >= duplicateWindowMs) {
      recentlyReportedErrors.delete(fingerprint);
    }
  });

  const errorDetail =
    error instanceof Error ? error.stack || error.toString() : serializeUnknownError(error);
  const fingerprint = [applicationId, payload.Message, errorDetail, payload.Url].join('|');

  if (recentlyReportedErrors.has(fingerprint)) return true;

  recentlyReportedErrors.set(fingerprint, now);
  return false;
};

export const reportBrowserError = (
  error: unknown,
  options: ReportBrowserErrorOptions = {}
): void => {
  try {
    if (axios.isAxiosError(error)) return;

    const url = options.url ?? window.location.href;
    const applicationId = options.applicationId ?? getApplicationIdFromUrl(url);

    if (!applicationId || !applicationIdPattern.test(applicationId)) return;

    const payload = createPayload(error, url, options);
    if (isDuplicateReport(applicationId, error, payload)) return;

    const sessionId = getSessionId();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      apiKey: config.apiKey,
      'x-grecaptcha': 'E7B81544-E48A-449F-A3C5-8EDB980C04A6',
    };

    if (sessionId) {
      headers['X-Session-Id'] = sessionId;
    }

    void axios
      .post(`${config.apiBaseUrl}/api/v2/policyapplications/${applicationId}/error`, payload, {
        headers,
      })
      .catch(() => undefined);
  } catch {
    // Error reporting must never interrupt the quote flow or report itself recursively.
  }
};

export const installGlobalBrowserErrorHandlers = (): (() => void) => {
  if (removeGlobalBrowserErrorHandlers) return removeGlobalBrowserErrorHandlers;

  const handleError = (event: ErrorEvent) => {
    const sourceLocation = event.filename
      ? `Source: ${event.filename}:${event.lineno}:${event.colno}`
      : undefined;

    reportBrowserError(event.error ?? event.message, {
      additionalDetail: sourceLocation,
      fallbackMessage: event.message || 'Unknown browser error',
    });
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    reportBrowserError(event.reason, {
      fallbackMessage: 'Unhandled promise rejection',
    });
  };

  window.addEventListener('error', handleError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  removeGlobalBrowserErrorHandlers = () => {
    window.removeEventListener('error', handleError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    removeGlobalBrowserErrorHandlers = null;
  };

  return removeGlobalBrowserErrorHandlers;
};
